import { createContext, useContext, useState, useEffect, useMemo } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOKEN_KEY = "campus_admin_token";

// Interceptor dinámico: lee el token de localStorage en cada petición.
// Más robusto que axios.defaults (funciona incluso si el token se renueva o
// Kubernetes ingress bloquea las cookies).
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers = config.headers || {};
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(res => setUser(res.data))
      .catch(() => {
        // Cookie failed - try bearer token from localStorage (already set as default header)
        if (localStorage.getItem(TOKEN_KEY)) {
          axios.get(`${API}/auth/me`)
            .then(res => setUser(res.data))
            .catch(() => {
              clearToken();
              setUser(null);
            });
        } else {
          setUser(null);
        }
      });
  }, []);

  const clearToken = () => {
    localStorage.removeItem(TOKEN_KEY);
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    const { access_token, ...userData } = res.data;
    // Guardar token en localStorage para que el interceptor lo incluya en cada petición
    if (access_token) {
      localStorage.setItem(TOKEN_KEY, access_token);
    }
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      // Logout errors are non-critical (session may already be expired)
      console.warn("Logout request failed (session may be expired):", err?.message);
    }
    clearToken();
    setUser(null);
  };

  // Memoize context value to avoid unnecessary re-renders of consumers
  const contextValue = useMemo(
    () => ({ user, login, logout, loading: user === undefined }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
